import React, { useState } from "react";
import { Link } from "react-router-dom";
import { animateScroll as scroll } from "react-scroll";
import { motion } from "framer-motion";
import { FiCalendar, FiChevronDown, FiChevronUp } from "react-icons/fi";
import {
  TRAINING_FEATURED_COURSES,
  TRAINING_SCHEDULED_PROGRAMS,
} from "../../data/trainingData";
import TrainingCourseCard from "./TrainingCourseCard";

const INITIAL_FEATURED_COUNT = 6;

const ProgramList = () => {
  const [showAllDates, setShowAllDates] = useState({});
  const [showAllFeatured, setShowAllFeatured] = useState(false);

  const toggleShowMore = (index) => {
    setShowAllDates((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleLinkClick = () => {
    scroll.scrollToTop();
  };

  const handleRegister = (title, range) => {
    const registrationDetails = {
      title,
      date: range,
      time: " ",
      venue: " ",
      fees: " ",
    };
    localStorage.setItem(
      "registrationDetails",
      JSON.stringify(registrationDetails)
    );
  };

  const visibleFeatured = showAllFeatured
    ? TRAINING_FEATURED_COURSES
    : TRAINING_FEATURED_COURSES.slice(0, INITIAL_FEATURED_COUNT);

  return (
    <>
      <section
        className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8"
        aria-label="Featured training courses"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-12 md:mb-16"
          >
            <p className="text-sm font-semibold uppercase tracking-wider text-[#F86624] mb-2">
              Featured Programs
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Explore Our Training Courses
            </h2>
            <div
              className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg">
              Browse our core training pathways and find the program that
              matches your professional goals and compliance requirements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {visibleFeatured.map((course, index) => (
              <TrainingCourseCard
                key={course.id}
                course={course}
                index={index}
                variant="featured"
              />
            ))}
          </div>

          {TRAINING_FEATURED_COURSES.length > INITIAL_FEATURED_COUNT && (
            <div className="flex justify-center mt-10">
              <button
                type="button"
                onClick={() => setShowAllFeatured((prev) => !prev)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--bg-primary)] hover:opacity-90 text-white text-sm font-semibold shadow-md transition-opacity"
              >
                {showAllFeatured ? "Show fewer courses" : "Show more courses"}
                {showAllFeatured ? (
                  <FiChevronUp size={16} aria-hidden="true" />
                ) : (
                  <FiChevronDown size={16} aria-hidden="true" />
                )}
              </button>
            </div>
          )}
        </div>
      </section>

      <section
        className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8"
        aria-label="Scheduled training programs"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Upcoming Public Programs
            </h2>
            <div
              className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg">
              Register for scheduled public training sessions. Select a date
              below to begin your registration.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {TRAINING_SCHEDULED_PROGRAMS.map((program, index) => (
              <article
                key={program.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:border-[var(--bg-primary)]/20 transition-all duration-300 flex flex-col h-full"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={program.image}
                    alt={`${program.title} training program`}
                    loading="lazy"
                    width={640}
                    height={360}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute bottom-3 left-4 text-xs font-semibold uppercase tracking-wide text-white/90 bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded-full">
                    {program.category}
                  </span>
                </div>

                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <h3 className="text-lg font-bold text-gray-900 leading-snug group-hover:text-[var(--bg-primary)] transition-colors">
                    <Link to={program.link} onClick={handleLinkClick}>
                      {program.title}
                    </Link>
                  </h3>

                  <Link
                    to={program.link}
                    onClick={handleLinkClick}
                    className="inline-flex w-fit mt-4 bg-[var(--bg-primary)] hover:opacity-90 duration-300 ease-in-out text-white py-2.5 px-5 rounded-full text-sm font-semibold shadow-md"
                  >
                    Program Details
                  </Link>

                  <div className="mt-5 pt-4 border-t border-gray-100 flex-1">
                    <h4 className="text-gray-500 font-semibold text-sm uppercase tracking-wide">
                      Available Dates
                    </h4>
                    <ul className="mt-3 space-y-2.5">
                      {program.dates
                        .slice(
                          0,
                          showAllDates[index] ? program.dates.length : 2
                        )
                        .map((date, dateIndex) => (
                          <li
                            key={dateIndex}
                            className="flex justify-between items-center gap-3 text-sm"
                          >
                            <span className="flex items-center text-gray-700 min-w-0">
                              <FiCalendar
                                size={15}
                                className="text-[var(--bg-primary)] mr-2 flex-shrink-0"
                                aria-hidden="true"
                              />
                              <span className="truncate">{date.range}</span>
                            </span>
                            <Link
                              to="/training-registration"
                              onClick={() => {
                                handleRegister(program.title, date.range);
                                handleLinkClick();
                              }}
                              className="flex-shrink-0 bg-gray-100 hover:bg-[var(--bg-primary)] hover:text-white duration-300 ease-in-out text-gray-800 py-1.5 px-3 rounded-full text-xs font-semibold"
                            >
                              Register Now
                            </Link>
                          </li>
                        ))}
                    </ul>

                    {program.dates.length > 2 && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => toggleShowMore(index)}
                          className="mt-4 text-[12px] text-white hover:opacity-90 duration-300 ease-in-out bg-[var(--bg-primary)]/80 py-2 px-3 rounded-full flex items-center font-medium"
                        >
                          {showAllDates[index] ? "Show less" : "Show more"}
                          {showAllDates[index] ? (
                            <FiChevronUp className="ml-1" size={14} />
                          ) : (
                            <FiChevronDown className="ml-1" size={14} />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProgramList;
